class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.querySelector('.before-after-slider__image-wrapper');
    this.beforeImage = this.querySelector('.before-after-slider__image--before');
    this.afterImage = this.querySelector('.before-after-slider__image--after');
    this.handle = this.querySelector('.before-after-slider__handle');
    this.isDragging = false;
    this.initialPosition = parseFloat(this.dataset.initialPosition) || 50;
    this.currentPosition = this.initialPosition;
    this.rafId = null;
    this.cachedRect = null;

    if (this.wrapper && this.beforeImage && this.afterImage && this.handle) {
      this.init();
    }
  }

  init() {
    // Set initial position
    this.updatePosition(this.initialPosition);

    // Add will-change hint for better performance
    this.beforeImage.style.willChange = 'clip-path';
    this.afterImage.style.willChange = 'clip-path';
    this.handle.style.willChange = 'left';

    // Mouse events
    this.wrapper.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));

    // Touch events - use passive where possible
    this.wrapper.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

    // Keyboard events
    this.handle.setAttribute('tabindex', '0');
    this.handle.addEventListener('keydown', this.onKeyDown.bind(this));

    // Cache rect on resize
    window.addEventListener('resize', () => {
      this.cachedRect = null;
    });

    // Prevent image dragging
    const images = this.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  onMouseDown(e) {
    this.isDragging = true;
    this.cachedRect = null; // Force recalculation
    this.wrapper.style.cursor = 'ew-resize';
    this.scheduleUpdate(e);
  }

  onMouseMove(e) {
    if (!this.isDragging) return;
    this.scheduleUpdate(e);
  }

  onMouseUp() {
    this.isDragging = false;
    this.wrapper.style.cursor = 'ew-resize';
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  onTouchStart(e) {
    this.isDragging = true;
    this.cachedRect = null; // Force recalculation
    this.scheduleUpdate(e.touches[0]);
  }

  onTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.scheduleUpdate(e.touches[0]);
  }

  onTouchEnd() {
    this.isDragging = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  onKeyDown(e) {
    let currentPosition = this.getCurrentPosition();
    let step = 5;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      currentPosition = Math.max(0, currentPosition - step);
      this.updatePosition(currentPosition);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      currentPosition = Math.min(100, currentPosition + step);
      this.updatePosition(currentPosition);
    }
  }

  scheduleUpdate(e) {
    // Cancel any pending update
    if (this.rafId) {
      return; // Already scheduled
    }

    // Schedule update using requestAnimationFrame
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updatePositionFromEvent(e);
    });
  }

  updatePositionFromEvent(e) {
    // Cache rect for performance
    if (!this.cachedRect) {
      this.cachedRect = this.wrapper.getBoundingClientRect();
    }
    
    const x = e.clientX - this.cachedRect.left;
    const percentage = (x / this.cachedRect.width) * 100;
    this.currentPosition = Math.max(0, Math.min(100, percentage));
    this.updatePosition(this.currentPosition);
  }

  updatePosition(percentage) {
    const rightInset = 100 - percentage;
    const leftInset = percentage;
    
    // Update before image clip-path (clips from right)
    this.beforeImage.style.clipPath = `inset(0 ${rightInset}% 0 0)`;
    
    // Update after image clip-path (clips from left)
    this.afterImage.style.clipPath = `inset(0 0 0 ${leftInset}%)`;
    
    // Update handle position
    this.handle.style.left = `${percentage}%`;
  }

  getCurrentPosition() {
    const clipPath = this.beforeImage.style.clipPath;
    const match = clipPath.match(/inset\(0 ([0-9.]+)% 0 0\)/);
    if (match) {
      return 100 - parseFloat(match[1]);
    }
    return this.initialPosition;
  }
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}